/**
 * ImportExcelModal - Modal for Excel file upload (Epic 18 - Phase 3)
 *
 * Allows users to upload .xlsx/.xls files with expected inventory
 * Integrates with POST /api/contagens/{job_id}/importar-excel
 */
import React, { useState } from 'react';
import { X, Upload, FileText, AlertCircle, ClipboardPaste } from 'lucide-react';
import contagemService from '../../services/contagemService';
import { msg } from '../../utils/messages';
import { tsvToXlsxFile, parseTsv } from '../../utils/pasteToXlsx';

interface ImportExcelModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: number;
  onImportSuccess: (data: any) => void;
}

export default function ImportExcelModal({
  isOpen,
  onClose,
  jobId,
  onImportSuccess,
}: ImportExcelModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // #15: modo de entrada — arquivo Excel OU colar do Excel (TSV)
  const [mode, setMode] = useState<'arquivo' | 'colar'>('arquivo');
  const [pastedText, setPastedText] = useState('');
  const pastedRows = parseTsv(pastedText).length;

  if (!isOpen) return null;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file extension
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setError('Arquivo deve ser Excel (.xlsx ou .xls)');
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    // #15: resolve o arquivo a partir do modo (arquivo OU colado→xlsx)
    let fileToSend: File | null = selectedFile;
    if (mode === 'colar') {
      fileToSend = tsvToXlsxFile(pastedText, `contagem_${jobId}.xlsx`);
      if (!fileToSend) {
        setError('Cole os dados do Excel primeiro');
        return;
      }
    } else if (!selectedFile) {
      setError('Selecione um arquivo primeiro');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const response = await contagemService.importarExcel(jobId, fileToSend as File);

      console.log('Upload success:', response);

      // Show success message
      msg.success(
        `Importação concluída! ${response.total_processadas} de ${response.total_linhas} linhas processadas (${response.taxa_sucesso.toFixed(0)}% sucesso)`
      );

      // Call success callback
      onImportSuccess(response);

      // Close modal
      handleClose();
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(
        err.response?.data?.error ||
          err.message ||
          'Erro ao fazer upload do arquivo'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPastedText('');
    setMode('arquivo');
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-green-600 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Upload size={24} />
            Importar Excel - Dados da Contagem
          </h2>
          <button
            onClick={handleClose}
            disabled={uploading}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Instructions */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              📋 Formato Esperado do Excel
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
              O arquivo Excel deve conter <strong>7 colunas obrigatórias</strong> (nesta ordem):
            </p>
            <ol className="text-sm text-gray-700 dark:text-gray-300 space-y-1 list-decimal list-inside">
              <li>
                <strong>Código do produto</strong> - Código interno do produto
              </li>
              <li>
                <strong>Descrição</strong> - Descrição completa do produto
              </li>
              <li>
                <strong>Lote</strong> - Número do lote
              </li>
              <li>
                <strong>Validade</strong> - Data de validade (DD/MM/AAAA ou AAAA-MM-DD)
              </li>
              <li>
                <strong>Quantidade</strong> - Quantidade esperada no sistema
              </li>
              <li>
                <strong>GTIN/EAN</strong> - Código de barras (opcional)
              </li>
              <li>
                <strong>Código do Cliente</strong> - Código do produto no cliente (opcional)
              </li>
            </ol>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-3 italic">
              💡 A primeira linha (cabeçalho) será ignorada automaticamente
            </p>
          </div>

          {/* #15: alternar entre Arquivo e Colar do Excel */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode('arquivo')}
              disabled={uploading}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${mode === 'arquivo' ? 'bg-emerald-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
            >
              <Upload size={16} /> Arquivo Excel
            </button>
            <button
              type="button"
              onClick={() => setMode('colar')}
              disabled={uploading}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${mode === 'colar' ? 'bg-emerald-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
            >
              <ClipboardPaste size={16} /> Colar do Excel
            </button>
          </div>

          {mode === 'arquivo' ? (
            /* File Input */
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Selecione o arquivo Excel:
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  disabled={uploading}
                  className="flex-1 text-sm text-gray-600 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 dark:file:bg-emerald-900/20 file:text-emerald-700 dark:file:text-emerald-400 hover:file:bg-emerald-100 dark:hover:file:bg-emerald-900/30 disabled:opacity-50 cursor-pointer"
                />
                {selectedFile && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <FileText size={16} />
                    <span className="font-medium">{selectedFile.name}</span>
                    <span className="text-xs text-gray-500">
                      ({(selectedFile.size / 1024).toFixed(2)} KB)
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* #15: Colar do Excel (TSV) */
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Selecione as colunas no Excel, copie (Ctrl+C) e cole abaixo (Ctrl+V):
              </label>
              <textarea
                value={pastedText}
                onChange={(e) => setPastedText(e.target.value)}
                disabled={uploading}
                rows={10}
                placeholder="Cole aqui as células copiadas do Excel..."
                className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
              />
              {pastedRows > 0 && (
                <p className="text-xs text-gray-500 mt-1">{pastedRows} linha(s) detectada(s) (a 1ª, cabeçalho, é ignorada).</p>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded flex items-start gap-3">
              <AlertCircle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-red-800 dark:text-red-400">
                  Erro na Importação
                </h4>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                  Enviando arquivo e processando dados...
                </span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleClose}
              disabled={uploading}
              className="px-6 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition font-medium disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleUpload}
              disabled={uploading || (mode === 'arquivo' ? !selectedFile : pastedRows === 0)}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-lg hover:from-emerald-600 hover:to-green-600 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload size={18} />
              {uploading ? 'Enviando...' : 'Importar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
